/**
 * Services catalog — R7.x cluster.
 * SST card is worked (links to multi-step form); other 8 are stubs.
 * Track My Requests promoted as top-level CTA.
 * Atlas: Services tab landing.
 */
"use client";

import Link from "next/link";
import { ArrowRight, ClipboardCheck } from "lucide-react";
import { SERVICES_STUBS, FIXTURE_REQUESTS, formatStubStatus } from "@petci/shared-ui";
import { useActivePersona } from "@/lib/persona";
import { useT } from "@/lib/locale";

export default function ServicesPage() {
  const persona = useActivePersona();
  const t = useT();
  const activeRequests = (FIXTURE_REQUESTS[persona.id] ?? []).filter(
    (r) => r.status.state === "in-progress" || r.status.state === "received" || r.status.state === "in-review",
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-neutral-900">
            {t("services.title")}
          </h1>
          <p className="text-sm text-neutral-700 mt-1">
            {t("services.subtitle")}
          </p>
        </div>
        <Link
          href="/services/track"
          className="btn-secondary text-sm flex-shrink-0 gap-1.5"
        >
          <ClipboardCheck size={15} aria-hidden />
          {t("services.track", { count: activeRequests.length })}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...SERVICES_STUBS].sort((a, b) => {
          if (a.status.kind === "live" && b.status.kind !== "live") return -1;
          if (a.status.kind !== "live" && b.status.kind === "live") return 1;
          return 0;
        }).map((stub) => {
          const workedHrefs: Record<string, string> = {
            "start-stop-transfer": "/services/start-stop-transfer/type",
            "damage-claims": "/services/damage-claims",
          };

          const stubName = stub.nameKey ? t(stub.nameKey) : stub.name;
          const stubDescription = stub.descriptionKey ? t(stub.descriptionKey) : stub.description;

          if (stub.status.kind === "live") {
            return (
              <Link
                key={stub.slug}
                href={workedHrefs[stub.slug] ?? `/services/${stub.slug}`}
                className="rounded-xl border-2 border-brand-primary bg-brand-primary/5 p-5 shadow-sm hover:shadow-card-hover transition-shadow duration-base group"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h2 className="text-base font-semibold text-neutral-900 group-hover:text-brand-primary-text transition-colors duration-fast">
                    {stubName}
                  </h2>
                  <span className="inline-flex items-center rounded-full bg-semantic-success/10 px-2.5 py-0.5 text-[10px] font-medium text-semantic-success-text whitespace-nowrap">
                    {t("stubs.availableNow")}
                  </span>
                </div>
                <p className="text-sm text-neutral-500 leading-relaxed line-clamp-2">
                  {stubDescription}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-primary-text group-hover:translate-x-0.5 transition-transform duration-fast">
                  {t("services.startRequest")} <ArrowRight size={14} aria-hidden />
                </span>
              </Link>
            );
          }

          return (
            <Link
              key={stub.slug}
              href={`/services/${stub.slug}`}
              className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm hover:shadow-card-hover transition-shadow duration-base group"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h2 className="text-base font-semibold text-neutral-900 group-hover:text-brand-primary-text transition-colors duration-fast">
                  {stubName}
                </h2>
                <span className="inline-flex items-center rounded-full bg-brand-secondary/10 px-2.5 py-0.5 text-[10px] font-medium text-brand-secondary whitespace-nowrap">
                  {formatStubStatus(stub.status, t).label}
                </span>
              </div>
              <p className="text-sm text-neutral-500 leading-relaxed line-clamp-2">
                {stubDescription}
              </p>
              <span className="mt-3 inline-flex text-xs font-medium text-brand-primary-text group-hover:translate-x-0.5 transition-transform duration-fast">
                {t("stubs.viewDetails")}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
